import type { CsvImportWarning } from "../types/customCsvImport";

const SEVERITY_RANK: Record<CsvImportWarning["severity"], number> = {
  error: 0,
  warning: 1,
  info: 2,
};

/** Lower numbers appear first within the same severity tier. */
const WARNING_CODE_ORDER: Record<string, number> = {
  FIFO_UNMATCHED_FILLS: 0,
  PNL_UNSUPPORTED_WITHOUT_COLUMN: 5,
  PNL_NEEDS_CONVERSION_RATE: 10,
  PNL_MISSING_CONTRACT_SPEC: 15,
  SYMBOL_NOT_IN_REGISTRY: 20,
  ASSET_CLASS_MISMATCH: 25,
  FIFO_NO_POSITION_EFFECT: 30,
  FIFO_SHORT_AMBIGUITY: 40,
  FIFO_PARTIAL_CLOSE_RISK: 50,
  FIFO_NO_POSITION_CODE: 60,
  STATEMENT_ACCOUNT_CURRENCY_UNKNOWN: 70,
};

const WARNING_DISPLAY_MESSAGES: Record<string, string> = {
  FIFO_NO_POSITION_CODE:
    "No position ID column is mapped. Tessera will create reference IDs for each trade automatically.",
  FIFO_NO_POSITION_EFFECT:
    "Your file doesn't mark which rows open a position and which close it. Tessera will pair buys and sells in the order they appear — the first buy matches the first sell, and so on.",
  FIFO_SHORT_AMBIGUITY:
    "Some rows may be short positions. Without a clear open/close column, Tessera may pair them incorrectly.",
  FIFO_PARTIAL_CLOSE_RISK:
    "Some closes may only partially close a position. Without a clear open/close column, Tessera may not split them correctly.",
  FIFO_UNMATCHED_FILLS:
    "Some buys or sells could not be paired. Fix your file or column mapping before importing.",
  PNL_NEEDS_CONVERSION_RATE:
    "Some FX trades are missing broker P&L and need a conversion rate. Map a conversion_rate column (e.g. OANDA BALANCE on the close row) or map broker-reported P&L instead.",
  PNL_MISSING_CONTRACT_SPEC:
    "Some CFD or futures trades are missing broker P&L and Tessera needs a contract size. Add contract_multipliers on the Account details step (e.g. SPX500 → 10) or map broker-reported P&L.",
  PNL_UNSUPPORTED_WITHOUT_COLUMN:
    "Tessera cannot compute P&L for some trades from prices alone. Map broker-reported P&L, conversion_rate, or contract_multipliers as appropriate.",
  SYMBOL_NOT_IN_REGISTRY:
    "Some symbols are not in Tessera's instrument registry. Check your symbol column mapping and traded asset classes, or map broker-reported P&L so price-based specs are not required.",
  ASSET_CLASS_MISMATCH:
    "Some symbols do not match the asset classes you selected. Update traded asset classes on the Account details step or fix symbol mapping.",
  STATEMENT_ACCOUNT_CURRENCY_UNKNOWN:
    "Account currency is not set. Add statement_account_currency on the Account details step (e.g. USD) so FX P&L checks can run accurately.",
};

const POSITION_ID_NO_LINK_RE =
  /position\s+id(?:s)?\s+(?:do(?:n't| not)|does(?:n't| not))\s+link/i;

const FILE_ORDER_PAIRING_MESSAGE =
  "The position ID values in your file don't connect each open row to its matching close. Tessera will pair buys and sells in the order they appear — the first buy matches the first sell, and so on.";

const FIFO_PAIRING_GROUP_CODES = new Set([
  "FIFO_NO_POSITION_EFFECT",
  "FIFO_NO_POSITION_CODE",
  "FIFO_SHORT_AMBIGUITY",
  "FIFO_PARTIAL_CLOSE_RISK",
]);

const PNL_CONFIGURATION_GROUP_CODES = new Set([
  "PNL_NEEDS_CONVERSION_RATE",
  "PNL_MISSING_CONTRACT_SPEC",
  "PNL_UNSUPPORTED_WITHOUT_COLUMN",
  "SYMBOL_NOT_IN_REGISTRY",
  "ASSET_CLASS_MISMATCH",
  "STATEMENT_ACCOUNT_CURRENCY_UNKNOWN",
]);

export type CsvImportWarningGroup =
  | { kind: "fifo_pairing"; warnings: CsvImportWarning[] }
  | { kind: "pnl_configuration"; warnings: CsvImportWarning[] }
  | { kind: "single"; warning: CsvImportWarning };

function warningSortKey(warning: CsvImportWarning): [number, number, string] {
  return [
    SEVERITY_RANK[warning.severity] ?? 99,
    WARNING_CODE_ORDER[warning.code] ?? 50,
    warning.code,
  ];
}

export function sortCsvImportWarnings(warnings: CsvImportWarning[]): CsvImportWarning[] {
  return [...warnings].sort((a, b) => {
    const [aSeverity, aOrder, aCode] = warningSortKey(a);
    const [bSeverity, bOrder, bCode] = warningSortKey(b);
    if (aSeverity !== bSeverity) return aSeverity - bSeverity;
    if (aOrder !== bOrder) return aOrder - bOrder;
    return aCode.localeCompare(bCode);
  });
}

export function displayCsvImportWarningMessage(warning: CsvImportWarning): string {
  if (POSITION_ID_NO_LINK_RE.test(warning.message)) {
    return FILE_ORDER_PAIRING_MESSAGE;
  }

  const byCode = WARNING_DISPLAY_MESSAGES[warning.code];
  if (byCode) return byCode;

  if (/\bFIFO\b/i.test(warning.message) || /file order/i.test(warning.message)) {
    return warning.message
      .replace(
        /pairing fills automatically in file order \(FIFO\)\.?/i,
        "pairing buys and sells in the order they appear — the first buy matches the first sell, and so on."
      )
      .replace(/\s*\(FIFO\)\.?/gi, ".")
      .replace(/\bFIFO\b/gi, "in file order")
      .replace(/\s{2,}/g, " ")
      .trim();
  }

  return warning.message;
}

export function csvImportWarningAlertStatus(
  severity: CsvImportWarning["severity"]
): "error" | "warning" | "info" {
  if (severity === "error") return "error";
  if (severity === "info") return "info";
  return "warning";
}

function isFifoPairingWarning(warning: CsvImportWarning): boolean {
  return (
    FIFO_PAIRING_GROUP_CODES.has(warning.code) ||
    POSITION_ID_NO_LINK_RE.test(warning.message)
  );
}

function isPnlConfigurationWarning(warning: CsvImportWarning): boolean {
  return PNL_CONFIGURATION_GROUP_CODES.has(warning.code);
}

export function groupCsvImportWarnings(warnings: CsvImportWarning[]): CsvImportWarningGroup[] {
  const sorted = sortCsvImportWarnings(warnings);
  const fifoPairing: CsvImportWarning[] = [];
  const pnlConfiguration: CsvImportWarning[] = [];
  const singles: CsvImportWarning[] = [];

  for (const warning of sorted) {
    if (isFifoPairingWarning(warning)) fifoPairing.push(warning);
    else if (isPnlConfigurationWarning(warning)) pnlConfiguration.push(warning);
    else singles.push(warning);
  }

  const groups: CsvImportWarningGroup[] = [];
  if (pnlConfiguration.length > 0) {
    groups.push({ kind: "pnl_configuration", warnings: pnlConfiguration });
  }
  if (fifoPairing.length > 0) {
    groups.push({ kind: "fifo_pairing", warnings: fifoPairing });
  }
  for (const warning of singles) {
    groups.push({ kind: "single", warning });
  }
  return groups;
}

export function highestWarningSeverity(
  warnings: CsvImportWarning[]
): CsvImportWarning["severity"] {
  if (warnings.some((w) => w.severity === "error")) return "error";
  if (warnings.some((w) => w.severity === "warning")) return "warning";
  return "info";
}

export type CsvImportWarningDisplayLine = {
  message: string;
  count: number;
  codes: string[];
  severity: CsvImportWarning["severity"];
};

/** Collapse repeated preview warnings (e.g. 93× SYMBOL_NOT_IN_REGISTRY) into one line. */
export function summarizeCsvImportWarningsForDisplay(
  warnings: CsvImportWarning[]
): CsvImportWarningDisplayLine[] {
  const lines: CsvImportWarningDisplayLine[] = []
  const indexByMessage = new Map<string, number>()

  for (const warning of sortCsvImportWarnings(warnings)) {
    const message = displayCsvImportWarningMessage(warning)
    const existingIndex = indexByMessage.get(message)
    if (existingIndex != null) {
      const existing = lines[existingIndex]!
      existing.count += 1
      if (!existing.codes.includes(warning.code)) {
        existing.codes.push(warning.code)
      }
      if (SEVERITY_RANK[warning.severity] < SEVERITY_RANK[existing.severity]) {
        existing.severity = warning.severity
      }
      continue
    }
    indexByMessage.set(message, lines.length)
    lines.push({
      message,
      count: 1,
      codes: [warning.code],
      severity: warning.severity,
    })
  }

  return lines
}

export function formatCsvImportWarningDisplayLine(line: CsvImportWarningDisplayLine): string {
  if (line.count <= 1) return line.message
  const unit = line.count === 1 ? "row" : "rows"
  return `${line.message} (${line.count} ${unit})`
}
